import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Legend,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { useState, useMemo } from "react";
import useSettings from "../../../hooks/Settings";
import AnalyticsEmptyState from "../../dashboard/AnalyticsEmptyState";
import { CHART_MARK_STROKE, CHART_MARK_STROKE_WIDTH } from '../../../helpers/chartPalette';

const MatrixMomentBarChart = ({
  data,
  lowerScale,
  upperScale,
  totalResponses,
  colors,
  projectHierarchy,
  graphColor
}) => {
  const [hoveredBar, setHoveredBar] = useState(null);
  // Per-project breakdown in the tooltip is portfolio-only.
  const portfolio = useSettings()?.portfolio;

  // ✅ Hooks must run before any early return
  const uniqueRowTitles = useMemo(
    () =>
      data && data[0]
        ? Object.keys(data[0]).filter((k) => k !== "name")
        : [],
    [data]
  );

  const hierarchyItems = useMemo(() => {
    if (!projectHierarchy) return [];
    const entries = Object.entries(projectHierarchy).map(([name, meta]) => ({
      name,
      depth: meta.depth ?? 0,
      path: meta.path ?? "0",
      totalResponses: meta.totalResponses ?? 0,
    }));
    const cmp = (a, b) => {
      const aP = a.path.split(".").map(Number);
      const bP = b.path.split(".").map(Number);
      const len = Math.max(aP.length, bP.length);
      for (let i = 0; i < len; i++) {
        const av = aP[i] ?? -1;
        const bv = bP[i] ?? -1;
        if (av !== bv) return av - bv;
      }
      return 0;
    };
    return entries.sort(cmp);
  }, [projectHierarchy]);

  // Now it’s safe to bail out
  if (!data || data.length === 0) {
    return <AnalyticsEmptyState />;
  }

  const percent = (num, den) =>
    den > 0 ? `${Math.round((num / den) * 100)}%` : "";

  const dummyData = [
    { name: "1", rowA: 2, rowB: 4, rowC: 3 },
    { name: "2", rowA: 3, rowB: 5, rowC: 2 },
    { name: "3", rowA: 1, rowB: 3, rowC: 4 },
  ];
  const dummyKeys = ["rowA", "rowB", "rowC"];
  const dummyMax = 6;

  const CustomizedLegend = ({ payload }) => (
    <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
      {payload.map((entry, index) => {
        const rowTitle = entry.dataKey;
        const legendColor = data[0][rowTitle]?.color || entry.color;
        return (
          <li
            key={`item-${index}`}
            onMouseEnter={() => setHoveredBar(rowTitle)}
            onMouseLeave={() => setHoveredBar(null)}
            style={{
              cursor: "pointer",
              fontWeight: hoveredBar === rowTitle ? "bold" : "normal",
              color: legendColor,
              lineHeight: "1.4",
            }}
          >
            {rowTitle}
          </li>
        );
      })}
    </ul>
  );

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;
    return (
      <div
        style={{
          background: "white",
          border: "1px solid rgba(0,0,0,0.15)",
          borderRadius: 8,
          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          padding: 12,
          maxWidth: 360,
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 6 }}>{label}</div>
        <div style={{ marginBottom: 8 }}>
          {payload.map((p, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
              <span style={{ opacity: 0.85 }}>{p.name}</span>
              <span style={{ fontWeight: 600 }}>{p.value ?? "—"}</span>
            </div>
          ))}
        </div>
        {portfolio === 'block' && (
          <>
        <hr style={{ border: 0, borderTop: "1px solid #eee", margin: "8px 0" }} />
        <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 4 }}>
          Bijdrage per project
        </div>
        <div style={{ maxHeight: 220, overflowY: "auto", paddingRight: 6 }}>
          {hierarchyItems.map((item) => {
            const count = item.totalResponses || 0;
            const dim = count === 0 ? 0.5 : 1;
            return (
              <div
                key={item.path}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  paddingLeft: 8 + item.depth * 14,
                  opacity: dim,
                  lineHeight: 1.4,
                }}
                title={`Pad: ${item.path}`}
              >
                <span>{item.name}</span>
                <span style={{ fontVariantNumeric: "tabular-nums" }}>
                  {count} {totalResponses ? `(${percent(count, totalResponses)})` : ""}
                </span>
              </div>
            );
          })}
        </div>
          </>
        )}
        {typeof totalResponses === "number" && (
          <>
            <hr style={{ border: 0, borderTop: "1px solid #eee", margin: "8px 0" }} />
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ opacity: 0.8 }}>Totaal antwoorden</span>
              <span style={{ fontWeight: 600 }}>{totalResponses}</span>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <>
    {data.length > 0 ? (
      <>
      <ResponsiveContainer width="100%" height={500}>
        <BarChart
          data={data.map((item) =>
            Object.fromEntries(
              Object.entries(item).map(([key, value]) => {
                if (key === "name") return [key, value];
                return [key, value?.value];
              })
            )
          )}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <XAxis 
          dataKey="name"
          stroke={graphColor || 'black'}
          tick={{ fill: graphColor || 'black' }} 
          axisLine={false}
          tickLine={false} 
          />
          <YAxis 
          domain={[0, upperScale]} 
          stroke={graphColor || 'black'}
          tick={{ fill: graphColor || 'black' }}
          axisLine={false}
          tickLine={false} 
          />
          <Tooltip content={(props) => <CustomTooltip {...props} />} />
          <Legend content={<CustomizedLegend />} verticalAlign="bottom" />
          {uniqueRowTitles.map((rowTitle, index) => (
            <Bar
              key={`bar-${index}`}
              dataKey={rowTitle}
              fill={data[0][rowTitle]?.color || "#206c7f"}
              fillOpacity={hoveredBar && hoveredBar !== rowTitle ? 0.3 : 1}
           stroke={CHART_MARK_STROKE} strokeWidth={CHART_MARK_STROKE_WIDTH} />
          ))}
        </BarChart>
      </ResponsiveContainer>
      </>
      ) : (
           <ResponsiveContainer width="90%" height={500}>
          <BarChart data={dummyData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <XAxis dataKey="name" stroke="lightgrey" tick={false} axisLine={false} tickLine={false} />
            <YAxis
              domain={[0, dummyMax]}
              stroke="lightgrey"
              tick={false}
              axisLine={false}
              tickLine={false}
            />
            {dummyKeys.map((key, i) => (
              <Bar
                key={`bar-dummy-${i}`}
                dataKey={key}
                fill="lightgrey"
                fillOpacity={1}
             stroke={CHART_MARK_STROKE} strokeWidth={CHART_MARK_STROKE_WIDTH} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      )}
    </>
  );
};

export default MatrixMomentBarChart;


